import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { SkillService } from '../services/skill.service';
import { ToastService } from 'services/toast.service';

import { DEFAULT_POST_SKILL, REGEX_RNCP_CODE } from '../constants/skill.constants';
import { ROUTES, SKILL_ID } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Client, RootState, Site, Skill, UserOfSkillLinked, UserFilter, Option, CommonGlobalService, MultiSelect } from '@umanforge/common';
import { ModifySkill,  } from 'interfaces/api_skill.interfaces';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { BodyConfirmModal } from '../components/modals/body/body_confirm_modal.component';
import { ILUOSkill } from '../components/users/iluo_skill.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Select } from '../components/inputs/select.component';
import { ColorPicker } from '../components/inputs/color_picker.component';
import { Loading } from '../components/loading.component';
import { ErrorModal } from 'components/modals/error_modal.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { BinIcon } from '../components/icons/bin_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';
import { SkillsIcon } from '../components/icons/skills_icon.component';

const toastService: ToastService = new ToastService( 'Details Skill' );
const defaultChangedUsers = { added: [], removed: [] };


export function DetailsSkillView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ skill, setSkill ] = useState<Skill | null>( null );
  const [ siteOfSkill, setSiteOfSkill ] = useState<Option<Site> | undefined>();
  const [ clientOfSite, setClientOfSite ] = useState<Option<Client> | undefined>();

  const [ modifySkill, setModifySkill ] = useState<ModifySkill>( {} );
  const [ changedUsers, setChangedUsers ] = useState<{ added: UserOfSkillLinked[], removed: UserOfSkillLinked[] }>( defaultChangedUsers );
  const [ defaultUsers, setDefaultUsers ] = useState<Option<UserOfSkillLinked>[]>( [] );
  const [ selectedUsers, setSelectedUsers ] = useState<Option<UserOfSkillLinked>[]>( [] );

  const [ canPut, setCanPut ] = useState<boolean>( false );
  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ useRncp, setUseRncp ] = useState<boolean>( false );
  const [ isAdminOfSite, setIsAdminOfSite ] = useState<boolean>( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshSkill();
    }
  }, [ me ] );

  useEffect( () => {
    if ( skill ) {
      checkCanPutSkill();
    }
  }, [ modifySkill, changedUsers, useRncp ] );

  useEffect( () => {
    const items = {
      added: selectedUsers
        .map( item => { return item.object; } )
        .filter( selectedUser => {
          return (
            ( !defaultUsers.find( currentUser => { return ( currentUser.object.id === selectedUser.id ); } ) ) ||
            ( defaultUsers.find( currentUser => { return ( currentUser.object.id === selectedUser.id ) && selectedUser.value !== currentUser.object.value; } ) )
          );
        } ),
      removed: defaultUsers
        .map( item => { return item.object; } )
        .filter( currentUser => { return !selectedUsers.find( selectedUser => { return selectedUser.object.id === currentUser.id; } ); } )
    };
    setChangedUsers( items );
  }, [ selectedUsers ] );

  /*------------------------- Requetes API User -------------------------*/
  const refreshSkill = async () => {
    setSkill( null );
    setIsLoading( true );
    setModifySkill( {} );
    if ( params.skillId ) {
      const refreshedSkill = await SkillService.getSkill( params.skillId, token, dispatch, () => { NavigationService.toSkills( navigate, false ); } );
      const refreshedSite = await SiteService.getSite( refreshedSkill.siteId, token, dispatch, () => { NavigationService.toSkills( navigate, false ); } );
      const refreshedClient = await ClientService.getClient( refreshedSite.clientId, token, dispatch, () => { NavigationService.toSkills( navigate, false ); } );
      const isAdmin: boolean = ( me != null && ( CommonGlobalService.isAdminOfSite( me, refreshedSite.id ) || CommonGlobalService.isAdminOfClient( me, refreshedClient.id ) ) );
      setIsAdminOfSite( isAdmin );
      const users = await UserService.getUsersOfSkill( params.skillId, token, dispatch, () => { NavigationService.toSkills( navigate, false ); } );
      const skillUsers: Option<UserOfSkillLinked>[] = CommonGlobalService.toOptions(
        users, isAdmin, !isAdmin,
        ( item1, item2 ) => {
          const value1 = item1.value ?? 1;
          const value2 = item2.value ?? 1;
          if ( value1 === value2 ) {
            return item1.fullname.toUpperCase() < item2.fullname.toUpperCase() ? -1 : 1;
          }
          return ( value1 < value2 ) ? -1 : 1;
        }
      );
      setSiteOfSkill( CommonGlobalService.toOptions( [ refreshedSite ] )[ 0 ] );
      setClientOfSite( CommonGlobalService.toOptions( [ refreshedClient ] )[ 0 ] );
      setDefaultUsers( structuredClone( skillUsers ) );
      setSelectedUsers( structuredClone( skillUsers ) );
      setSkill( refreshedSkill );
      setUseRncp( !( refreshedSkill.rncpCode === DEFAULT_POST_SKILL.rncpCode && refreshedSkill.rncpLevel === DEFAULT_POST_SKILL.rncpLevel ) );
      setIsLoading( false );
    }
  };

  const deleteSkill = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      setIsLoading( true );
      let isDeleted = false;
      if ( params.skillId ) {
        isDeleted = await SkillService.deleteSkill( params.skillId, token, dispatch, refreshSkill, refreshSkill );
      }
      if ( isDeleted ) {
        toastService.showInfo( t( 'The {0} skill has been delete.' ).replace( '{0}', ( skill?.name ?? t( 'The skill' ) ) ) );
        NavigationService.toSkills( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putSkill = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isSkillNameChange: boolean =  modifySkill.name != null && modifySkill.name != '' && modifySkill.name != skill?.name;
      const isSkillColorChange: boolean =  modifySkill.color != null && modifySkill.color != '' && modifySkill.color != skill?.color;
      const isSkillRncpChange: boolean =  (
        (
          modifySkill.rncpLevel !== undefined && modifySkill.rncpLevel != skill?.rncpLevel &&
          ( ( modifySkill.rncpCode != null && REGEX_RNCP_CODE.test( modifySkill.rncpCode ) ) || modifySkill.rncpCode == null )
        ) ||
        (
          modifySkill.rncpCode !== undefined && modifySkill.rncpCode !== skill?.rncpCode &&
          ( ( modifySkill.rncpCode != null && REGEX_RNCP_CODE.test( modifySkill.rncpCode ) ) || modifySkill.rncpCode == null )
        )
      );
      if ( params.skillId && ( isSkillNameChange || isSkillColorChange || isSkillRncpChange ) ) {
        isUpdated = await SkillService.putSkill( params.skillId, modifySkill, token, dispatch, refreshSkill, refreshSkill );
      }
      resolve( isUpdated );
    } );
  };

  const addUsersToSkill = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( skill?.id && changedUsers.added.length > 0 ) {
        const body = { users: changedUsers.added.map( user => { return { id: user.id, value: user.value }; } ) };
        isUpdated = await SkillService.postUsersToSkill( skill?.id, body, token, dispatch, refreshSkill, refreshSkill );
      }
      resolve( isUpdated );
    } );
  };

  const removedUsersToSkill = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( skill?.id && changedUsers.removed.length > 0 ) {
        const body = { users: changedUsers.removed.map( user => { return user.id; } ) };
        isUpdated = await SkillService.deleteUsersToSkill( skill?.id, body, token, dispatch, refreshSkill, refreshSkill );
      }
      resolve( isUpdated );
    } );
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserOfSkillLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    if ( siteOfSkill != null ) {
      filter.filterSites = [ siteOfSkill.value ];
    }

    const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
    return { total: result.total, list: CommonGlobalService.toOptions<UserOfSkillLinked>(
      result.users.map<UserOfSkillLinked>( user => {
        return {
          id: user.id,
          fullname: user.fullname,
          isDisable: user.isDisable,
          isAdminOfUser: user.isAdminOfUser,
          value: selectedUsers.find( selectedUser => { return selectedUser.value === user.id; } )?.object.value ?? 1,
        };
      } ) )
    };
  };

  /*---------------------------- Functions ------------------------------*/
  const onSaveClick = async () => {
    let isUserModify = false;
    setIsLoading( true );
    if ( skill ) {
      const isPutSkill = await putSkill();
      const isAddUsersToSkill = await addUsersToSkill();
      const isRemovedUsersToSkill = await removedUsersToSkill();
      isUserModify = isPutSkill ||
        isAddUsersToSkill ||
        isRemovedUsersToSkill;
    }
    if ( isUserModify ) {
      toastService.showInfo( t( 'The {0} skill has been modify.' ).replace( '{0}', ( skill?.name ?? t( 'The skill' ) ) ) );
      await refreshSkill();
    }
    setIsLoading( false );
  };

  const checkCanPutSkill = () => {
    let requirement = false;

    if ( useRncp ) {
      requirement = ( modifySkill.name != '' && ( modifySkill.rncpCode == null || REGEX_RNCP_CODE.test( modifySkill.rncpCode ) ) );
    } else {
      requirement = ( modifySkill.name != '' );
    }

    const isSkillNameChange: boolean =  modifySkill.name != null && modifySkill.name != skill?.name;
    const isSkillColorChange: boolean =  modifySkill.color != null && modifySkill.color != skill?.color;
    const isSkillRncpChange: boolean =  (
      (
        modifySkill.rncpLevel !== undefined && modifySkill.rncpLevel != skill?.rncpLevel &&
        ( ( modifySkill.rncpCode != null && REGEX_RNCP_CODE.test( modifySkill.rncpCode ) ) || modifySkill.rncpCode == null )
      ) ||
      (
        modifySkill.rncpCode !== undefined && modifySkill.rncpCode !== skill?.rncpCode &&
        ( ( modifySkill.rncpCode != null && REGEX_RNCP_CODE.test( modifySkill.rncpCode ) ) || modifySkill.rncpCode == null )
      )
    );
    const isUsersChange: boolean = changedUsers.added.length > 0 || changedUsers.removed.length > 0;
    setCanPut(
      requirement && (
        isSkillNameChange ||
        isSkillColorChange ||
        isSkillRncpChange ||
        isUsersChange
      )
    );
    setHasChanged(
      isSkillNameChange ||
      isSkillColorChange ||
      isSkillRncpChange ||
      isUsersChange
    );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChanged } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Modify skill' ) } needConfirmationToNav={ hasChanged }/>
        <Breadcrumb
          needConfirmationToNav={ hasChanged }
          links={
            [
              { label: t( 'common:skill' ), route: ROUTES.SKILLS, icon: ( <SkillsIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: skill ? skill.name : t( 'Modify skill' ), route: ROUTES.SKILL.replace( SKILL_ID, params.skillId ?? '' ), icon: ( <SkillsIcon className='h-[14px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading && skill ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ clientOfSite }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ [ clientOfSite ] }
                  />
                </div>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ siteOfSkill }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ [ siteOfSkill ] }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_SKILL_NAME }
                    label={ t( 'Skill name' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'Authorization' ) + '...' }
                    icon={ <SkillsIcon className='w-7 h-7' /> }
                    value={ skill.name ?? ''}
                    onChange={ newSkillName => {
                      modifySkill.name = skill?.name != newSkillName ? newSkillName : undefined;
                      setModifySkill( structuredClone( modifySkill ) );
                    } }
                  />
                </div>

                <div className='w-1/4 min-w-[225px] self-start'>
                  <ColorPicker
                    label={ t( 'Colour of skill' ) }
                    required
                    disabled={ !isAdminOfSite }
                    value={ skill.color ?? '' }
                    onBlur={ newSkillColor => {
                      modifySkill.color = skill?.color.toUpperCase() != newSkillColor.toUpperCase() ? newSkillColor : undefined;
                      setModifySkill( structuredClone( modifySkill ) );
                    } }
                  />
                </div>

                {/*
                  <div className='w-full'>
                    {
                      isAdminOfSite ?
                        <div className='-mx-[8px]'>
                          <Button
                            id='rncp-tooltip'
                            disable={ !isAdminOfSite }
                            label={ t( 'RNCP qualification' ) }
                            color={ useRncp ? '' : '#f87171' }
                            colorHover={ useRncp ? '' : '#ef4444' }
                            icon={ useRncp ? <CheckIcon className='h-5 w-5 fill-white'/> : <CrossIcon className='h-5 w-5 fill-white'/> }
                            onClick={ () => {
                              let code: string | null | undefined = null;
                              let level: number | null | undefined = null;
                              if ( !useRncp && skill ) {
                                code = skill?.rncpCode ?? '';
                                level = skill?.rncpLevel ?? MIN_RNCP_LEVEL_SKILL;
                              }
                              modifySkill.rncpCode = skill?.rncpCode != code ? code : undefined;
                              modifySkill.rncpLevel = skill?.rncpLevel != level ? level : undefined;
                              setModifySkill( structuredClone( modifySkill ) );
                              setUseRncp( !useRncp );
                            } }
                          />
                          <Tooltip
                            anchorSelect='#rncp-tooltip'
                            className='z-[1000] max-w-[600px] text-wrap'
                            place="right"
                          >
                            <span>
                              { t( 'National Directory of Professional Certifications' ) }
                            </span>
                          </Tooltip>
                        </div>
                        :
                        null
                    }
                    <div className={`flex w-full gap-2 origin-top-left transition-all duration-500 ${useRncp ? 'scale-100 opacity-100 max-h-96' : 'scale-50 opacity-0 max-h-0'}`}>
                      <div className='w-4/5'>
                        <Input
                          key={ useRncp ? 1 : 0 }
                          required
                          maxLength={ MAX_LENGTH_ENTITY.UF_SKILL_RNCP_CODE }
                          disabled={ !isAdminOfSite }
                          onlyUpperCase
                          label={ t( 'RNCP code' ) }
                          placeholder='RNCPXXXXX'
                          icon={ <SkillsIcon className='w-7 h-7' /> }
                          value={ skill.rncpCode ?? '' }
                          checkContent={ ( text: string ): string | null => {
                            if ( !REGEX_RNCP_CODE.test( text ) ) {
                              return 'The code format must be RNCPXXXXX';
                            } else {
                              return null;
                            }
                          } }
                          onChange={ newSkillRncpCode => {
                            modifySkill.rncpCode = skill?.rncpCode != newSkillRncpCode ? newSkillRncpCode : undefined;
                            setModifySkill( structuredClone( modifySkill ) );
                          } }
                        />
                      </div>
                      <div className='w-1/5'>
                        <InputNumber
                          key={ useRncp ? 1 : 0 }
                          disabled={ !isAdminOfSite }
                          required
                          min={ MIN_RNCP_LEVEL_SKILL }
                          max={ MAX_RNCP_LEVEL_SKILL }
                          label={ t( 'Level' ) }
                          value={ skill.rncpLevel ?? MIN_RNCP_LEVEL_SKILL }
                          onChange={ newSkillRncpLevel => {
                            modifySkill.rncpLevel = skill?.rncpLevel != newSkillRncpLevel ? newSkillRncpLevel : undefined;
                            setModifySkill( structuredClone( modifySkill ) );
                          } }
                        />
                      </div>
                    </div>
                  </div>
                */}

                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'User' ) + '(' + selectedUsers.length + ')' }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedUsers }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedUsers( options );
                    } }
                    extra={ user => {
                      return (
                        <ILUOSkill
                          skill={ {
                            id: user.object.id,
                            siteId: skill.siteId,
                            name: skill.name,
                            rncpCode: skill.rncpCode,
                            rncpLevel: skill.rncpLevel,
                            color: skill.color,
                            value: user.object.value
                          } }
                          onChange={ changedSkill => {
                            user.object.value = changedSkill.value;
                            const findedUser = selectedUsers.find( item => { return changedSkill.id == item.object.id;  } );
                            if ( findedUser ) {
                              findedUser.object.value = changedSkill.value;
                            }
                            setSelectedUsers( structuredClone( selectedUsers ) );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex justify-center w-full h-14 mt-10'>
                  {
                    isAdminOfSite ?
                      <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                        <Button
                          label={ t( 'Delete' ) }
                          icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                          color='#f87171'
                          colorHover='#ef4444'
                          onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                        />
                      </div>
                      :
                      null
                  }
                  <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                    <Button
                      label={ t( 'save' ) }
                      disable={ !canPut }
                      icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                      color='#0bb173'
                      colorHover='#088D5C'
                      onClick={ onSaveClick }
                    />
                  </div>
                </div>
              </form>

              { confirmDeleteModalIsOpen ?
                <ErrorModal
                  title={ t( 'Deletion of {0} !' ).replace( '{0}', skill.name )  }
                  isOpen={ confirmDeleteModalIsOpen }
                  onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
                >
                  <BodyConfirmModal
                    onCancel={ () => { setConfirmDeleteModalIsOpen( false ); } }
                    onConfirm={ async () => {
                      const isDeleted = await deleteSkill();
                      if ( isDeleted ) {
                        NavigationService.toSkills( navigate, false );
                      }
                      setConfirmDeleteModalIsOpen( false );
                    } }
                  >
                    <span className='flex flex-col w-full h-full text-[14px] font-[475] text-gray-600 p-3'>
                      { t( 'Are you sure you want to delete this skill ?' ) }
                    </span>
                  </BodyConfirmModal>
                </ErrorModal>
                :
                null
              }
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}